import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MatterDetailed } from '../../../../types/matters';
import { taskApi } from '../../../tasks/api/taskApi';
import { TaskDetailModal } from '../../../tasks/components/TaskDetailModal';
import type { Task } from '../../../tasks/types/task.types';
import GanttView from './GanttView';
import GanttTaskDetailPanel from './gantt/GanttTaskDetailPanel';

interface TimelineTabProps {
  matter: MatterDetailed;
}

type ViewMode = 'list' | 'gantt';

interface PhaseGroup {
  phaseName: string;
  phaseOrder: number;
  tasks: Task[];
}

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  todo: { label: 'To Do', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  review: { label: 'In Review', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
  done: { label: 'Done', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  archived: { label: 'Archived', className: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500' },
};

function isOverdue(task: Task): boolean {
  if (!task.due_date || task.status === 'done' || task.status === 'archived') return false;
  return new Date(task.due_date) < new Date();
}

function isUpcoming(task: Task): boolean {
  if (!task.due_date || task.status === 'done' || task.status === 'archived') return false;
  const due = new Date(task.due_date);
  const now = new Date();
  const sevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return due >= now && due <= sevenDays;
}

const TimelineTab: React.FC<TimelineTabProps> = ({ matter }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [contentVisible, setContentVisible] = useState(true);
  const [renderKey, setRenderKey] = useState(0);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set());
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [editModalTaskId, setEditModalTaskId] = useState<string | null>(null);

  const switchView = useCallback((mode: ViewMode) => {
    if (mode === viewMode) return;
    setContentVisible(false);
    setTimeout(() => {
      setViewMode(mode);
      setRenderKey((k) => k + 1);
      requestAnimationFrame(() => setContentVisible(true));
    }, 150);
  }, [viewMode]);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const result = await taskApi.getByEngagement(matter.id);
      const data = result as any;
      setTasks(data.tasks || []);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, [matter.id]);

  useEffect(() => {
    if (tasks.length === 0) {
      fetchTasks();
    }
  }, [tasks.length, fetchTasks]);

  // Group tasks by phase — same logic as GanttView
  const phaseGroups = useMemo((): PhaseGroup[] => {
    const groups = new Map<string, PhaseGroup>();
    const ungrouped: Task[] = [];

    for (const task of tasks) {
      const phaseName = task.court_metadata?.phase_name;
      if (phaseName) {
        if (!groups.has(phaseName)) {
          groups.set(phaseName, {
            phaseName,
            phaseOrder: task.court_metadata?.phase_order ?? 999,
            tasks: [],
          });
        }
        groups.get(phaseName)!.tasks.push(task);
      } else {
        ungrouped.push(task);
      }
    }

    // Sort tasks within each phase by due_date
    for (const group of groups.values()) {
      group.tasks.sort((a, b) => {
        const aDate = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const bDate = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        return aDate - bDate;
      });
    }

    const sorted = Array.from(groups.values()).sort((a, b) => a.phaseOrder - b.phaseOrder);
    if (ungrouped.length > 0) {
      ungrouped.sort((a, b) => {
        const aDate = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const bDate = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        return aDate - bDate;
      });
      sorted.push({ phaseName: 'Other Tasks', phaseOrder: 9999, tasks: ungrouped });
    }
    return sorted;
  }, [tasks]);

  // Compute stats
  const stats = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'done').length;
    const inProgress = tasks.filter(t => t.status === 'in_progress' || t.status === 'review').length;
    const overdue = tasks.filter(isOverdue).length;
    const upcoming = tasks.filter(isUpcoming).length;
    const courtMandated = tasks.filter(t => t.court_metadata?.is_court_mandated).length;
    return { total, completed, inProgress, overdue, upcoming, courtMandated };
  }, [tasks]);

  const togglePhase = (phaseName: string) => {
    setCollapsedPhases(prev => {
      const next = new Set(prev);
      if (next.has(phaseName)) {
        next.delete(phaseName);
      } else {
        next.add(phaseName);
      }
      return next;
    });
  };

  const handleSelectTask = useCallback((taskId: string | null) => {
    setSelectedTaskId(taskId);
  }, []);

  const handleListRowClick = useCallback((taskId: string) => {
    setSelectedTaskId(prev => prev === taskId ? null : taskId);
  }, []);

  const handleCheckboxClick = useCallback(async (task: Task, e: React.MouseEvent) => {
    e.stopPropagation();
    const newStatus = task.status === 'done' ? 'todo' : 'done';
    try {
      await taskApi.updateStatus(task.id, newStatus);
      fetchTasks();
    } catch {
      // Silently fail
    }
  }, [fetchTasks]);

  const formatDueDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6 min-w-0">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white truncate min-w-0">Engagement Timeline</h3>
        {/* View mode toggle */}
        <div className="flex-shrink-0 flex items-center rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
          <button
            onClick={() => switchView('list')}
            className={`px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${
              viewMode === 'list'
                ? 'bg-lex-accent text-lex-accent-contrast'
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            List View
          </button>
          <button
            onClick={() => switchView('gantt')}
            className={`px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${
              viewMode === 'gantt'
                ? 'bg-lex-accent text-lex-accent-contrast'
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            Gantt View
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent" />
          <span className="ml-3 text-gray-500 dark:text-gray-400">Loading tasks...</span>
        </div>
      )}

      {/* Content — both views share the same task data */}
      {!loading && (
        <>
          {/* Case Plan Stats — universal metrics (always 6 columns via CSS Grid) */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-6 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Total Tasks</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.completed}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Completed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.inProgress}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">In Progress</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${stats.overdue > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-600'}`}>
                  {stats.overdue}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Overdue</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.upcoming}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Upcoming</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">{stats.courtMandated}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Mandated</div>
              </div>
            </div>
          </div>

          {/* ── View + Detail Panel flex wrapper ── */}
          <div
            key={renderKey}
            className="flex gap-0"
            style={{
              opacity: contentVisible ? 1 : 0,
              transition: 'opacity 150ms ease-out',
            }}
          >
            {/* Active view — shrinks when panel opens */}
            <div className="flex-1 min-w-0">
              {/* Gantt View */}
              {viewMode === 'gantt' && (
                <GanttView
                  tasks={tasks}
                  matterId={matter.id}
                  selectedTaskId={selectedTaskId}
                  onSelectTask={handleSelectTask}
                  onTasksUpdated={fetchTasks}
                />
              )}

              {/* List View — phased task list */}
              {viewMode === 'list' && (
                <div className="space-y-3">
                  {phaseGroups.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-5xl mb-4 text-gray-400">&#9776;</div>
                      <div className="text-xl font-medium text-gray-900 dark:text-white mb-2">No Tasks Yet</div>
                      <div className="text-gray-500 dark:text-gray-400">
                        Tasks will appear here once a task template is applied to this engagement.
                      </div>
                    </div>
                  )}

                  {phaseGroups.map((group) => {
                    const completedCount = group.tasks.filter(t => t.status === 'done').length;
                    const completionPct = group.tasks.length > 0
                      ? Math.round((completedCount / group.tasks.length) * 100)
                      : 0;
                    const isCollapsed = collapsedPhases.has(group.phaseName);

                    return (
                      <div
                        key={group.phaseName}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                      >
                        {/* Phase header */}
                        <button
                          onClick={() => togglePhase(group.phaseName)}
                          className="w-full flex items-center justify-between px-4 py-2.5 bg-lex-accent-soft hover:bg-lex-state-selected-hover transition-colors text-left"
                        >
                          <div className="flex items-center gap-2">
                            <svg
                              className={`w-4 h-4 text-lex-accent transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-sm font-semibold text-lex-accent">
                              {group.phaseName}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              &mdash; {group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Completion bar */}
                            <div className="hidden sm:flex items-center gap-2">
                              <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-green-500 rounded-full transition-all"
                                  style={{ width: `${completionPct}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right">
                                {completionPct}%
                              </span>
                            </div>
                          </div>
                        </button>

                        {/* Task rows */}
                        {!isCollapsed && (
                          <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            {group.tasks.map((task) => {
                              const overdue = isOverdue(task);
                              const badge = STATUS_BADGES[task.status] || STATUS_BADGES.todo;
                              const forms = task.court_metadata?.required_forms || [];
                              const mandated = task.court_metadata?.is_court_mandated;
                              const isSelected = selectedTaskId === task.id;

                              return (
                                <div
                                  key={task.id}
                                  className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                                    isSelected
                                      ? 'bg-lex-accent-soft border-l-2 border-l-lex-accent'
                                      : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                                  }`}
                                  onClick={() => handleListRowClick(task.id)}
                                >
                                  {/* Status checkbox — click toggles done/todo, does NOT open panel */}
                                  <div
                                    className="flex-shrink-0 cursor-pointer"
                                    onClick={(e) => handleCheckboxClick(task, e)}
                                  >
                                    {task.status === 'done' ? (
                                      <div className="w-5 h-5 rounded border-2 border-green-500 bg-green-500 flex items-center justify-center">
                                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                      </div>
                                    ) : (
                                      <div className={`w-5 h-5 rounded border-2 ${
                                        overdue ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'
                                      } hover:border-lex-accent transition-colors`} />
                                    )}
                                  </div>

                                  {/* Task name */}
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-sm ${
                                      task.status === 'done'
                                        ? 'text-gray-400 dark:text-gray-500 line-through'
                                        : 'text-gray-900 dark:text-gray-100'
                                    } truncate block`}>
                                      {task.title}
                                    </span>
                                  </div>

                                  {/* Court mandated indicator */}
                                  {mandated && (
                                    <span
                                      className="flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                      title="Court Mandated"
                                    >
                                      Mandated
                                    </span>
                                  )}

                                  {/* Form references */}
                                  {forms.length > 0 && (
                                    <div className="flex-shrink-0 flex gap-1">
                                      {forms.slice(0, 2).map((form) => (
                                        <span
                                          key={form.form_number}
                                          className="text-xs px-1.5 py-0.5 rounded bg-lex-accent-soft text-lex-accent"
                                          title={form.form_name}
                                        >
                                          {form.form_number}
                                        </span>
                                      ))}
                                      {forms.length > 2 && (
                                        <span className="text-xs text-gray-400">+{forms.length - 2}</span>
                                      )}
                                    </div>
                                  )}

                                  {/* Status badge */}
                                  <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
                                    {badge.label}
                                  </span>

                                  {/* Assignee */}
                                  {task.assignee && (
                                    <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 hidden lg:block max-w-[120px] truncate">
                                      {task.assignee.full_name || task.assignee.email}
                                    </span>
                                  )}

                                  {/* Due date */}
                                  <span className={`flex-shrink-0 text-xs w-[90px] text-right ${
                                    overdue
                                      ? 'text-red-600 dark:text-red-400 font-medium'
                                      : 'text-gray-500 dark:text-gray-400'
                                  }`}>
                                    {formatDueDate(task.due_date)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Detail Panel ── slides in from right, shared by both views */}
            {selectedTaskId && (
              <GanttTaskDetailPanel
                taskId={selectedTaskId}
                matterId={matter.id}
                onClose={() => setSelectedTaskId(null)}
                onTaskUpdated={fetchTasks}
                onEditTask={(taskId) => setEditModalTaskId(taskId)}
              />
            )}
          </div>
        </>
      )}

      {/* Task Edit Modal — opened from Gantt panel "Edit Task" action */}
      <TaskDetailModal
        isOpen={!!editModalTaskId}
        taskId={editModalTaskId}
        onClose={() => setEditModalTaskId(null)}
        onUpdated={fetchTasks}
      />
    </div>
  );
};

export default TimelineTab;
